import { useState, useMemo, useEffect, ChangeEvent, useRef, Fragment, RefObject } from 'react';
import tw from 'twin.macro';
import { CSSProp } from 'styled-components';
import { Trans } from 'react-i18next';

import { CaretIcon } from '@icons/CaretIcon';
import useOnClickOutside from '@app/hooks/useOnClickOutside';

const Container = tw.div`relative inline-block`;
const SelectOptionContainer = tw.div`flex flex-col w-[220px] h-[270px] overflow-auto bg-neutral-background border border-solid border-gray-1 rounded shadow-ui absolute top-9 left-0 overflow-x-hidden z-10 gap-0.5`;
const SearchInput = tw.input`block w-full text-xs placeholder:text-xs placeholder:italic leading-[14px] py-[9px] px-2.5 focus:border-0 focus:outline-none bg-neutral-background`;
const Divider = tw.div`w-auto border-b border-solid border-gray-1 h-[1px]`;
export const OptionWrapper = tw.div`relative hover:bg-gray-2 rounded-sm min-h-[29px] flex justify-between items-center cursor-pointer`;
export const Option = tw.div`cursor-pointer truncate px-2 py-[6px] text-[11px] leading-4 font-medium`;
const Prefix = tw.span`-mr-1`;
const SelectLabel = tw.div`
  w-auto h-[28px] grid grid-cols-[1fr_14px] gap-1 items-center border border-solid border-gray-1 pr-2 rounded cursor-pointer shadow-ui bg-neutral-background
  [> div]:border-none
`;
const StickyTopWrapper = tw.div`sticky top-0 z-10 bg-neutral-background`;
const StickyBottomWrapper = tw.div`flex sticky bottom-0 justify-center items-center bg-neutral-background z-10 pb-[2px]`;

const MIN_SEARCH_LENGTH = 2;
const MAX_ITEMS_TO_SHOW = 7;

const emptyContextualOption: AppleCategoriesDropdownOption = {
  value: null,
  label: (
    <Option tw='text-text-and-icon-muted-2 opacity-50'>
      {<Trans i18nKey='insights.query_builder.apple.empty_option'>Choose Apple Category</Trans>}
    </Option>
  ),
};

const ArrowToRight = () => (
  <div tw='pr-[8px] '>
    <CaretIcon tw='fill-text-and-icon -rotate-90' />
  </div>
);
const ScrollArrowIndicator = ({
  pointTo,
  onMouseEnter,
  onMouseLeave,
}: {
  pointTo: 'top' | 'bottom';
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) => {
  return (
    <div
      tw='flex justify-center items-center py-[4px] w-full'
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      <CaretIcon
        tw='fill-text-and-icon-muted-2 max-w-[10px] max-h-[10px]'
        css={[pointTo === 'top' && tw`rotate-180`]}
        style={{ transition: '' }}
      />
    </div>
  );
};

function filterOptionsByVale(
  options: AppleCategoriesDropdownOption[],
  value: string
): AppleCategoriesDropdownOption[] {
  const filteredOptions: AppleCategoriesDropdownOption[] = [];
  for (const option of options) {
    if (option.value?.toLowerCase().includes(value.toLowerCase())) {
      filteredOptions.push(option);
    }
    if (option.children) {
      const filteredChildren = filterOptionsByVale(option.children, value);
      if (filteredChildren.length) {
        filteredOptions.push(...filteredChildren);
      }
    }
  }
  return filteredOptions;
}

export interface AppleCategoriesDropdownOption {
  value: string | null;
  label: string | JSX.Element;
  children?: AppleCategoriesDropdownOption[];
}

interface IDropdownProps {
  options: AppleCategoriesDropdownOption[];
  value: AppleCategoriesDropdownOption | null;
  onChange: (val: AppleCategoriesDropdownOption | AppleCategoriesDropdownOption[]) => void;
  id?: string;
  searchPlaceholder?: string;
  autoFocus?: boolean;
  labelStyle?: CSSProp;
  caretStyle?: CSSProp;
  isPresentational?: boolean;
  labelPrefix?: string;
  onClickOutside?: (selectedOption: AppleCategoriesDropdownOption | null) => void;
}

export const AppleCategoriesDropdown = ({
  id,
  value,
  options,
  searchPlaceholder = 'Search',
  onChange,
  autoFocus = false,
  labelStyle,
  caretStyle,
  isPresentational = false,
  labelPrefix = '',
  onClickOutside,
  ...rest
}: IDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState<boolean>(autoFocus);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useOnClickOutside(dropdownRef, () => {
    setIsFocused(false);
    onClickOutside && onClickOutside(selectedOption);
  });

  const [labelValue, setLabelValue] = useState(value);
  const [selectedOption, setSelectedOption] = useState<AppleCategoriesDropdownOption | null>(value);
  const [currentTier1Category, setCurrentTier1Category] =
    useState<AppleCategoriesDropdownOption | null>(null);
  const [currentTier2Category, setCurrentTier2Category] =
    useState<AppleCategoriesDropdownOption | null>(null);

  const tier1ContentRef = useRef<HTMLDivElement>(null);
  const tier2ContentRef = useRef<HTMLDivElement>(null);

  let scrollInterval: NodeJS.Timeout;

  const tier2IsEnabled = useMemo(
    () => currentTier1Category && !!currentTier1Category.children?.length,
    [currentTier1Category]
  );

  const shouldShowTier2ScrollArrows = useMemo(
    () =>
      currentTier1Category?.children?.length &&
      currentTier1Category?.children?.length > MAX_ITEMS_TO_SHOW,
    [currentTier1Category]
  );

  const handleMouseEnter = (
    contentElement: RefObject<HTMLDivElement>,
    pointTo: 'top' | 'bottom'
  ) => {
    if (!contentElement.current) return;

    scrollInterval = setInterval(() => {
      if (!contentElement.current) return;
      contentElement.current.scrollTop += pointTo === 'top' ? -1.5 : 1.5;
    }, 10);
  };

  const handleMouseLeave = () => {
    clearInterval(scrollInterval);
  };

  const _options = useMemo(() => {
    if (searchTerm.length < MIN_SEARCH_LENGTH) {
      return options;
    }

    return filterOptionsByVale(options, searchTerm);
  }, [searchTerm, options]);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setSearchTerm(value);
    handleMouseLeave();
  };

  useEffect(() => {
    setCurrentTier1Category(null);
    setCurrentTier2Category(null);
  }, [searchTerm]);

  useEffect(() => {
    setLabelValue(value || emptyContextualOption);
  }, [value]);

  useEffect(() => {
    if (selectedOption && onChange) {
      onChange(selectedOption);
      setSearchTerm('');
    }
  }, [selectedOption]);

  useEffect(() => {
    if (autoFocus) {
      setIsFocused(true);
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!isFocused) {
      setCurrentTier1Category(null);
      setCurrentTier2Category(null);
    }
  }, [isFocused]);

  // close tier 2 dropdown when tier 1 is changed
  useEffect(() => {
    if (currentTier1Category?.value) {
      setCurrentTier2Category(null);
    }
  }, [currentTier1Category]);

  return (
    <Container id={id} ref={dropdownRef}>
      <SelectLabel
        css={[
          isFocused && tw`border-accent-muted`,
          labelStyle,
          isPresentational && tw`cursor-auto [> div]:cursor-auto!`,
          labelPrefix.length && tw`grid-cols-[auto_1fr_14px]`,
        ]}
        onClick={() => !isPresentational && setIsFocused(!isFocused)}
        {...rest}>
        {labelPrefix && <Prefix>{labelPrefix}</Prefix>}
        {labelValue?.label}
        {!isPresentational && (
          <CaretIcon tw='fill-text-and-icon-muted w-2.5 h-2.5' css={[caretStyle]} />
        )}
      </SelectLabel>

      {isFocused && (
        <SelectOptionContainer ref={tier1ContentRef}>
          <StickyTopWrapper>
            <SearchInput
              autoFocus
              data-testid='dropdown-search'
              type='text'
              placeholder={searchPlaceholder}
              onChange={(e) => handleSearch(e as ChangeEvent<HTMLInputElement>)}
            />
            <Divider />
            {_options.length > MAX_ITEMS_TO_SHOW && (
              <ScrollArrowIndicator
                pointTo='top'
                onMouseEnter={() => handleMouseEnter(tier1ContentRef, 'top')}
                onMouseLeave={handleMouseLeave}
              />
            )}
          </StickyTopWrapper>

          {searchTerm.length < MIN_SEARCH_LENGTH
            ? _options.map((option: AppleCategoriesDropdownOption, index) => (
                <Fragment key={`${option.value}`}>
                  <OptionWrapper
                    css={[currentTier1Category?.value === option.value && tw`bg-gray-2`]}
                    onClick={() => {
                      setSelectedOption(option);
                      setIsFocused(false);
                    }}
                    onMouseEnter={() => setCurrentTier1Category(option)}
                    key={index}>
                    {option.label}
                    {!!option.children?.length && <ArrowToRight />}
                  </OptionWrapper>
                </Fragment>
              ))
            : _options.map((option: AppleCategoriesDropdownOption, index) => (
                <Fragment key={`${option.value}`}>
                  <OptionWrapper
                    css={[currentTier1Category?.value === option.value && tw`bg-gray-2`]}
                    onClick={() => {
                      setSelectedOption(option);
                      setIsFocused(false);
                    }}
                    onMouseEnter={() => setCurrentTier1Category(option)}
                    key={index}>
                    <div tw='flex truncate items-center px-[4px]'>{option.label}</div>

                    {!!option.children?.length && <ArrowToRight />}
                  </OptionWrapper>
                </Fragment>
              ))}

          {_options.length > MAX_ITEMS_TO_SHOW && (
            <StickyBottomWrapper>
              <ScrollArrowIndicator
                pointTo='bottom'
                onMouseEnter={() => handleMouseEnter(tier1ContentRef, 'bottom')}
                onMouseLeave={handleMouseLeave}
              />
            </StickyBottomWrapper>
          )}
        </SelectOptionContainer>
      )}

      {tier2IsEnabled && (
        <SelectOptionContainer tw='left-[224px]' ref={tier2ContentRef}>
          <StickyTopWrapper>
            {shouldShowTier2ScrollArrows && (
              <ScrollArrowIndicator
                pointTo='top'
                onMouseEnter={() => handleMouseEnter(tier2ContentRef, 'top')}
                onMouseLeave={handleMouseLeave}
              />
            )}
          </StickyTopWrapper>

          {currentTier1Category?.children?.map((option: AppleCategoriesDropdownOption, index) => (
            <Fragment key={option.value}>
              <OptionWrapper
                css={[currentTier2Category?.value === option.value && tw`bg-gray-2`]}
                onClick={() => {
                  setSelectedOption(option);
                  setIsFocused(false);
                }}
                onMouseEnter={() => setCurrentTier2Category(option)}
                key={index}>
                {option.label}
                {!!option.children?.length && <ArrowToRight />}
              </OptionWrapper>
            </Fragment>
          ))}

          {shouldShowTier2ScrollArrows && (
            <StickyBottomWrapper>
              <ScrollArrowIndicator
                pointTo='bottom'
                onMouseEnter={() => handleMouseEnter(tier2ContentRef, 'bottom')}
                onMouseLeave={handleMouseLeave}
              />
            </StickyBottomWrapper>
          )}
        </SelectOptionContainer>
      )}
    </Container>
  );
};
